import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface LoginPageProps {
  onLogin: (username: string, password: string) => Promise<void>;
}

export function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setRetryAfter(null);
    setLoading(true);
    try {
      await onLogin(username, password);
      navigate('/hoy', { replace: true });
    } catch (err: any) {
      const msg = err?.message ?? 'Credenciales inválidas';

      // Detect 429 (rate limited)
      if (msg.includes('Demasiados intentos') || msg.includes('429')) {
        setError(msg);
        // Extract retryAfter from the message
        const match = msg.match(/Esperá\s+(\d+)\s+segundos/);
        if (match) {
          setRetryAfter(parseInt(match[1], 10));
        }
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-600 to-brand-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <img src="/erp/dobby.png" alt="StockeArte" className="w-24 h-24 object-contain mx-auto mb-2" />
          <h1 className="text-2xl font-bold text-slate-800">Stockearte ERP</h1>
          <p className="text-slate-500 text-sm mt-1">
            Sistema de gestión integral
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div
              className={`px-4 py-2 rounded-lg border text-sm ${
                retryAfter
                  ? 'bg-amber-50 text-amber-800 border-amber-200'
                  : 'bg-red-50 text-red-700 border-red-200'
              }`}
              role="alert"
            >
              {error}
              {retryAfter && (
                <span className="block mt-1 text-xs opacity-75">
                  Podés intentar nuevamente en {retryAfter} segundos.
                </span>
              )}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Usuario
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition"
              placeholder="tu.usuario"
              autoFocus
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Contraseña
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 outline-none transition"
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-brand-600 hover:bg-brand-500 disabled:bg-brand-300 text-white font-medium py-2.5 rounded-lg transition-colors"
          >
            {loading ? 'Ingresando...' : 'Ingresar'}
          </button>
        </form>
      </div>
    </div>
  );
}
